import type { ProblemTemplate } from '../../types.ts';
import { exactDiv, integerProblem, LETTER_PAIRS, span, UNKNOWNS } from './helpers-g05-08.ts';

/**
 * Grade 6 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 6: input → output number sentences, two-step equations by
 * inverse operations and x ÷ a written as a fraction. Whole numbers only — no
 * negatives in prompts or answers. Multiplication is always written with ×
 * (Intermediate Phase notation): "4 × m", never the Senior Phase "4m"; adding
 * and subtracting like terms is Grade 8 (see g6.like-terms-coef in grade08.ts).
 */

/** `p × v` in Intermediate Phase notation: a coefficient of 1 is left out (`v`), never implied (`pv`). */
function times(p: number, v: string): string {
  return p === 1 ? v : `${p} \\times ${v}`;
}

const substitute: ProblemTemplate = {
  id: 'g6.substitute',
  grade: 6,
  title: 'Substitute into a number sentence: a × x ± b or a × (x + b)',
  parTimeMs: 10000,
  allowNegative: false,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const p = rng.int(2, span(difficulty, 5, 12));
    const value = rng.int(span(difficulty, 1, 2), span(difficulty, 5, 12));
    const forms = ['plus', ...(difficulty >= 0.35 ? ['minus'] : []), ...(difficulty >= 0.6 ? ['bracket'] : [])];
    const form = rng.pick(forms);
    let prompt: string;
    let answer: number;
    let distractors: (number | null)[];
    if (form === 'bracket') {
      const q = rng.int(1, span(difficulty, 5, 12));
      prompt = times(p, `(${v} + ${q})`);
      answer = p * (value + q);
      distractors = [
        p * value + q, // ignored the brackets: multiplied first, then added
        p + value + q, // used + for the ×
      ];
    } else {
      const qMax = span(difficulty, 9, 30);
      const sign = form === 'minus' ? -1 : 1;
      // b < p·value keeps the result ≥ 1 for the "−" form.
      const q = sign < 0 ? rng.int(1, Math.min(qMax, p * value - 1)) : rng.int(1, qMax);
      const sq = sign * q;
      prompt = `${times(p, v)} ${sign < 0 ? '-' : '+'} ${q}`;
      answer = p * value + sq;
      distractors = [
        p + value + sq, // used + for the ×
        p * (value + sq), // order of operations: did the ± before the ×
        p * value - sq, // sign slip on the constant
        p * value, // stopped after the × step
      ];
    }
    return integerProblem({
      prompt,
      instruction: { key: 'evaluate', params: { assignMath: `${v} = ${value}` } },
      answer,
      allowNegative: false,
      distractors,
    });
  },
};

const fracEq: ProblemTemplate = {
  id: 'g6.frac-eq',
  grade: 6,
  title: 'Solve x/a = b',
  parTimeMs: 9000,
  allowNegative: false,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(2, span(difficulty, 5, 12));
    const b = rng.int(2, span(difficulty, 6, 15));
    return integerProblem({
      prompt: `\\frac{${v}}{${a}} = ${b}`,
      instruction: { key: 'solve', params: { v } },
      answer: a * b,
      allowNegative: false,
      distractors: [
        exactDiv(b, a), // inverse-operation error: divided again instead of multiplying
        a + b, // undid the division by adding
        a * (b - 1), // times-table slip: one group short
      ],
    });
  },
};

const twoStep: ProblemTemplate = {
  id: 'g6.two-step',
  grade: 6,
  title: 'Solve a × x + b = c',
  parTimeMs: 13000,
  allowNegative: false,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(2, span(difficulty, 4, 12));
    const x = rng.int(2, span(difficulty, 6, 15));
    const b = rng.int(1, span(difficulty, 9, 40));
    const c = a * x + b;
    const constantFirst = difficulty >= 0.4 && rng.chance(0.3);
    const lhs = constantFirst ? `${b} + ${times(a, v)}` : `${times(a, v)} + ${b}`;
    const quotient = exactDiv(c, a);
    return integerProblem({
      prompt: `${lhs} = ${c}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: false,
      distractors: [
        c - b, // subtracted b but forgot to divide by a
        c - b - a, // undid the × by subtracting a instead of dividing
        exactDiv(c + b, a), // inverse-operation slip: added b instead of subtracting it
        quotient === null ? null : quotient - b, // divided before subtracting (wrong order)
      ],
    });
  },
};

const twoStepSub: ProblemTemplate = {
  id: 'g6.two-step-sub',
  grade: 6,
  title: 'Solve a × x − b = c',
  parTimeMs: 13000,
  allowNegative: false,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(2, span(difficulty, 4, 12));
    const x = rng.int(2, span(difficulty, 6, 15));
    // b < ax keeps c ≥ 1.
    const b = rng.int(1, Math.min(span(difficulty, 9, 40), a * x - 1));
    const c = a * x - b;
    const quotient = exactDiv(c, a);
    return integerProblem({
      prompt: `${times(a, v)} - ${b} = ${c}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: false,
      distractors: [
        c + b, // added b back but forgot to divide by a
        c + b - a, // undid the × by subtracting a instead of dividing
        exactDiv(c - b, a), // inverse-operation slip: subtracted b instead of adding it
        quotient === null ? null : quotient + b, // divided before adding (wrong order)
      ],
    });
  },
};

const substituteTwo: ProblemTemplate = {
  id: 'g6.substitute-two',
  grade: 6,
  title: 'Substitute two values: evaluate p × a ± q × b or a × b + r',
  parTimeMs: 12000,
  allowNegative: false,
  generate(rng, difficulty) {
    const [u, w] = rng.pick(LETTER_PAIRS);
    const vMax = span(difficulty, 6, 10);
    const cMax = span(difficulty, 5, 9);
    const forms = [
      'pa+b',
      'a+qb',
      ...(difficulty >= 0.3 ? ['pa+qb'] : []),
      ...(difficulty >= 0.5 ? ['pa-qb'] : []),
      ...(difficulty >= 0.7 ? ['ab+r'] : []),
    ];
    const form = rng.pick(forms);

    let p: number;
    let q: number;
    let va: number;
    let vb: number;
    if (form === 'pa-qb') {
      // p > q and a > b guarantee pa − qb ≥ 1 without retries. p = q is avoided because
      // "8 × a − 8 × b" with a − b = 1 would make the answer the coefficient shown in the prompt.
      q = rng.int(2, cMax - 1);
      p = rng.int(q + 1, cMax);
      vb = rng.int(2, vMax - 1);
      va = rng.int(vb + 1, vMax);
    } else {
      p = form === 'a+qb' ? 1 : rng.int(2, cMax);
      q = form === 'pa+b' ? 1 : rng.int(2, cMax);
      va = rng.int(2, vMax);
      vb = rng.intExcept(2, vMax, [va]);
    }
    const sq = form === 'pa-qb' ? -q : q;
    const r = rng.int(1, span(difficulty, 9, 20));

    const sign = Math.sign(sq);
    /** The expression's value with the given numbers in place of u and w. */
    const value = (x: number, y: number) => (form === 'ab+r' ? x * y + r : p * x + sq * y);
    /** "3 × a" read as 3 + a: the coefficient added to the value instead of multiplied. */
    const added = (coef: number, x: number) => (coef === 1 ? x : coef + x);

    const prompt =
      form === 'ab+r' ? `${u} \\times ${w} + ${r}` : `${times(p, u)} ${sign < 0 ? '-' : '+'} ${times(q, w)}`;
    const distractors =
      form === 'ab+r'
        ? [
            va + vb + r, // added the two values instead of multiplying them
            va * (vb + r), // order of operations: added before multiplying
            va * vb - r, // sign slip on the constant
          ]
        : [
            value(vb, va), // swapped the values: put a's number in for b and vice versa
            added(p, va) + sign * added(q, vb), // used + for the ×
            q === 1 ? null : (p * va + sq) * vb, // worked strictly left to right, ignoring order of operations
            form === 'pa-qb' ? p * va + q * vb : null, // sign slip: added the second term
          ];
    return integerProblem({
      prompt,
      // \sep, not a comma: a comma next to numbers reads as a decimal comma in Afrikaans.
      instruction: { key: 'evaluate', params: { assignMath: `${u} = ${va} \\sep ${w} = ${vb}` } },
      answer: value(va, vb),
      allowNegative: false,
      distractors,
    });
  },
};

export const GRADE_06: ProblemTemplate[] = [substitute, fracEq, twoStep, twoStepSub, substituteTwo];
